"use client"
import PrimaryButton from "@/components/buttons/PrimaryButton"
import SecondaryButton from "@/components/buttons/SecondaryButton"
import type { DialogProps } from "@relume_io/relume-ui"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
} from "@relume_io/relume-ui"
import { ReactNode } from "react"
import { twMerge } from "tailwind-merge"
import TextLink from "../text/TextLink"

type ModalProps = DialogProps & {
  title?: string
  buttonText: string
  childComponent?: ReactNode
  description?: ReactNode
  footer: ReactNode
  type?: "primary" | "secondary" | "link"
  buttonClassName?: string
  modalClassName?: string
  triggerId?: string
  closeButtonOnFooter?: boolean
}

const Modal: React.FC<ModalProps> = ({
  title,
  buttonText,
  description,
  childComponent,
  footer,
  type = "primary",
  buttonClassName,
  modalClassName,
  closeButtonOnFooter = false,
  triggerId = "open-dialog",
  ...dialogProps
}) => {
  return (
    <Dialog {...dialogProps}>
      <DialogTrigger asChild>
        {type === "primary" ? (
          <PrimaryButton id={triggerId} className={twMerge("w-auto", buttonClassName)}>
            {buttonText}
          </PrimaryButton>
        ) : type === "secondary" ? (
          <SecondaryButton id={triggerId} className={twMerge("w-auto", buttonClassName)}>
            {buttonText}
          </SecondaryButton>
        ) : (
          <TextLink id={triggerId} href={""} className={twMerge("w-auto", buttonClassName)}>
            {buttonText}
          </TextLink>
        )}
      </DialogTrigger>
      <DialogPortal>
        <DialogOverlay className="bg-black/50" />
        <DialogContent className={twMerge("w-full max-w-md bg-white p-6 lg:p-10 md:p-12 rounded-xl")}>
          <DialogHeader>
            <DialogTitle className="font-bricolage font-extrabold text-[2.25rem] md:text-[3rem] leading-[100%]">
              {title}
            </DialogTitle>
            {!!description && <DialogDescription>{description}</DialogDescription>}
            <div className={twMerge("childComponent max-h-[600px] overflow-y-auto overflow-x-visible", modalClassName)}>
              {childComponent}
            </div>
          </DialogHeader>
          <DialogFooter>
            {footer}
            {closeButtonOnFooter && (
              <DialogTrigger asChild>
                <div className={"p-2"}>
                  <PrimaryButton variant="small" onClick={() => null}>
                    Ga verder
                  </PrimaryButton>
                </div>
              </DialogTrigger>
            )}
          </DialogFooter>
        </DialogContent>
      </DialogPortal>
    </Dialog>
  )
}

export default Modal
